import * as Mui from '@material-ui/core';
import { Add, Close } from '@material-ui/icons';
import { FieldArray, Form, Formik } from 'formik';
import { useMemo, useRef } from 'react';
import { InputField } from '../../../../../components/form';
import { RuleTitle } from '../../../canteen-management/canteen/view-components/price-rule/components/rule-title';
import { CloseDrawer } from '../../../../../components/form/close-drawer';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { ConfirmationPopover } from '../../../../../components/form/warning-popover';

const paperProps = {
    style: {
        width: '550px',
        height: '100%',
        overflow: 'hidden'
    }
}

export const CreateCategoryDrawer = ({ onClose, formikProps, ...props }) => {

    const formilRef = useRef(null)
    const { t } = useTranslation()

    const { existingParentNames, existingSubNames } = useMemo(() => {
        const categories = formikProps?.values?.categories || [];

        return {
            existingParentNames: categories.map(c =>
                c.parentCategory?.name?.trim().toLowerCase()
            ).filter(Boolean),

            existingSubNames: categories.flatMap(c =>
                c.subCategories?.map(s => s.name?.trim().toLowerCase()) || []
            )
        };
    }, [formikProps]);


    const onSubmit = (values) => {
        const categoryData = values.categories.map((item, index) => {
            const uniqueId = uuidv4();
            return ({
                color: (c => ({ bg: c, color: parseInt(c.slice(1), 16) > 0xffffff / 2 ? '#000' : '#fff' }))('#' + Math.floor(Math.random() * 16777215).toString(16)), //random color 
                parentCategory: {
                    name: item.parentCategory,
                    id: uniqueId
                },
                subCategories: item.subCategories.split(',')
                    .map(item => item.trim())
                    .filter(item => item !== "")
                    .map(item => {
                        const uniqueId = uuidv4();
                        return ({
                            id: uniqueId,
                            name: item,
                            products: []
                        })
                    })
            })
        })
        formikProps.setFieldValue('categories', [...formikProps.values.categories, ...categoryData])
        onClose()

    }

    const validationSchema = Yup.object().shape({
        categories: Yup.array().of(
            Yup.object().shape({
                parentCategory: Yup.string()
                    .required(t("AUTH.VALIDATION.REQUIRED_FIELD"))
                    .test('unique-parent-global', t("ORGANIZATION.ONBOARD.CATEGORY_DRAWER.NAME_EXISTS"), function (value) {
                        if (!value) return true;
                        const inputVal = value.trim().toLowerCase();

                        // Check 1: Is it in the PREVIOUSLY ADDED list?
                        if (existingParentNames.includes(inputVal)) {
                            return this.createError({ message: t("ORGANIZATION.ONBOARD.CATEGORY_DRAWER.NAME_EXISTS_IN_LIST", { name: value }) });
                        }

                        // Check 2: Is it duplicated in the CURRENT form?
                        const { categories } = this.from[1].value;
                        const duplicates = categories.filter(c =>
                            c.parentCategory &&
                            c.parentCategory.trim().toLowerCase() === inputVal
                        );

                        return duplicates.length <= 1;
                    }),

                subCategories: Yup.string()
                    .required(t("AUTH.VALIDATION.REQUIRED_FIELD"))
                    .test('unique-sub-global', t("ORGANIZATION.ONBOARD.CATEGORY_DRAWER.SUB_CATEGORIES_MUST_BE_UNIQUE"), function (value) {
                        if (!value) return true;

                        // A. Get items currently being typed
                        const currentInputItems = value.split(',')
                            .map(i => i.trim())
                            .filter(i => i !== "");

                        // B. Get all LOCAL subcategories (currently in this drawer)
                        const { categories } = this.from[1].value;
                        const allLocalSubCats = categories.flatMap(cat =>
                            (cat.subCategories || "")
                                .split(',')
                                .map(s => s.trim().toLowerCase())
                                .filter(s => s !== "")
                        );

                        // C. Validation Loop
                        for (let item of currentInputItems) {
                            const itemLower = item.toLowerCase();

                            // Check 1: Is it in the PREVIOUSLY ADDED list?
                            if (existingSubNames.includes(itemLower)) {
                                return this.createError({
                                    message: t("ORGANIZATION.ONBOARD.CATEGORY_DRAWER.NAME_EXISTS_IN_PREVIOUSLY_ADDED_CATEGORY", { name: item }),
                                    path: this.path
                                });
                            }

                            // Check 2: Is it duplicated in the CURRENT form?
                            // We count occurrences in the local form
                            const localCount = allLocalSubCats.filter(localItem =>
                                localItem === itemLower
                            ).length;

                            if (localCount > 1) {
                                return this.createError({
                                    message: t("ORGANIZATION.ONBOARD.CATEGORY_DRAWER.SUB_CATEGORIES_MUST_BE_UNIQUE_IN_LIST", { name: item }),
                                    path: this.path
                                });
                            }
                        }

                        return true;
                    }),
            })
        )
    })

    return <Mui.Drawer
        {...props}
        onClose={() => { }}
        anchor="right"
        PaperProps={paperProps}

    >
        <Mui.Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Mui.Box className="pl-2 pr-2 pt-2 pb-2 d-flex justify-content-between align-items-center">
                <RuleTitle title={t("ORGANIZATION.ONBOARD.CATEGORY_DRAWER.TITLE")} subtitle={t("ORGANIZATION.ONBOARD.CATEGORY_DRAWER.SUBTITLE")} />
                <CloseDrawer onClose={() => { onClose() }} formikRef={formilRef} />
            </Mui.Box>
            <Mui.Divider className="mb-2" />
            <Mui.Box style={{ flex: 1, overflowY: 'auto', scrollbarGutter: 'stable' }}>
                <Formik
                    innerRef={formilRef}
                    initialValues={{
                        categories: [
                            {
                                parentCategory: '',
                                subCategories: ''
                            }
                        ],
                    }}
                    validationSchema={validationSchema}
                    onSubmit={onSubmit}
                >
                    {(props) => (
                        <Form className="default-form p-2">
                            <FieldArray
                                name="categories"
                                render={(arrayHelpers) => (
                                    <>
                                        {props.values.categories.map((item, index) => (
                                            <Mui.Box key={index} style={{ position: 'relative' }}>
                                                {index > 0 &&

                                                    <ConfirmationPopover
                                                        component={(handleClose) =>
                                                            <Mui.Box
                                                                style={{ position: 'absolute', top: '-1rem', right: '-1rem', cursor: 'pointer', height: '3rem', width: '3rem', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', backgroundColor: '#ff0000', padding: '1rem' }}
                                                                onClick={(e) => {
                                                                    if (item.parentCategory === '' && item.subCategories === '') {
                                                                        arrayHelpers.remove(index)
                                                                    } else {
                                                                        handleClose(e)
                                                                    }
                                                                }}
                                                            >
                                                                <Close style={{ fontSize: '2.5rem', color: 'white' }} />
                                                            </Mui.Box>
                                                        }
                                                        title={t("CANTEEN.CHANGES_DETECTED")}
                                                        description={t("CANTEEN.CHANGES_DETECTED_DESC")}
                                                        onConfirm={() => {
                                                            arrayHelpers.remove(index)
                                                        }}
                                                    />
                                                }
                                                <Mui.Grid
                                                    container
                                                    className="mb-2 p-2"
                                                    style={{
                                                        backgroundColor: '#fafafa90',
                                                        borderRadius: '1rem',
                                                        border: '1px solid #9aa1a730'
                                                    }}
                                                >
                                                    <Mui.Grid item xs={12} className="form-group" >
                                                        <InputField
                                                            name={`categories.${index}.parentCategory`}
                                                            type="text"
                                                            label={t("ORGANIZATION.ONBOARD.CATEGORY_DRAWER.PARENT_LABEL")}
                                                        />
                                                    </Mui.Grid>
                                                    <Mui.Grid item xs={12} className="form-group" >
                                                        <InputField
                                                            name={`categories.${index}.subCategories`}
                                                            type="text"
                                                            label={t("ORGANIZATION.ONBOARD.CATEGORY_DRAWER.SECTION_LABEL")}
                                                        />
                                                    </Mui.Grid>
                                                </Mui.Grid>
                                            </Mui.Box>
                                        ))}
                                        <Mui.Box
                                            className='pointer d-flex justify-content-center align-items-center w-100'
                                            sx={{
                                                border: '1px solid #392ed6',
                                                borderStyle: 'dashed',
                                                borderRadius: '1rem',
                                                padding: '1rem',
                                                '&:hover': {
                                                    backgroundColor: '#fafafa90',
                                                }
                                            }}
                                            onClick={() => arrayHelpers.push({ parentCategory: "", subCategories: "" })}
                                        >
                                            <Mui.Box
                                                className='d-flex align-items-center '
                                                gridGap={10}
                                            >
                                                <Add style={{ fontSize: '2rem', border: '1.5px solid #392ed6', color: '#392ed6', borderRadius: '50%' }} />
                                                <Mui.Typography
                                                    component="h5"
                                                    variant="h5"
                                                    className="font-weight-medium text-color-primary"
                                                >
                                                    {t("ORGANIZATION.ONBOARD.CATEGORY_DRAWER.ADD_ANOTHER")}
                                                </Mui.Typography>
                                            </Mui.Box>
                                        </Mui.Box>
                                    </>
                                )}
                            />
                        </Form>
                    )}
                </Formik>
            </Mui.Box>
            <Mui.Divider />
            <Mui.Box className='d-flex justify-content-end p-2'>
                <CloseDrawer
                    onClose={() => { onClose() }}
                    formikRef={formilRef}
                    customComponent={(handleClose) => (
                        <Mui.Button
                            className='btn-default mr-2'
                            variant='outlined'
                            color='primary'
                            onClick={handleClose}
                        >
                            {t("ORGANIZATION.ONBOARD.CATEGORY_DRAWER.CANCEL")}
                        </Mui.Button>
                    )}
                />
                <Mui.Button
                    className='btn-default'
                    variant='contained'
                    color='primary'
                    onClick={() => formilRef.current.handleSubmit()}
                >
                    {t("ORGANIZATION.ONBOARD.CATEGORY_DRAWER.CREATE")}
                </Mui.Button>
            </Mui.Box>
        </Mui.Box>
    </Mui.Drawer >
}